import { activeRouteStore } from '@customerio/hydra-nav-state';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { m } from 'framer-motion';
import type { MouseEvent } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useActiveBundle } from '../../../../context/active-bundle';
import { useCurrentWorkspace } from '../../../../context/current-workspace';
import { useSafeNavigate } from '../../../../context/safe-navigate';
import { GlobalCSSVariable, useThemeContext } from '../../../../context/theme';
import { AccountFeatureFlag, accountStore, authStore, hasFeatureFlag } from '../../../../stores/auth';
import { AppBundle } from '../../../../utils/app-bundles';
import type { NavItem } from './routes';
import { generateRoutes, isNavItemActive } from './routes';
import './main-nav.css';

import ExpandCollapseButton from '@/features/navigation/components/expand-collapse-button';
import { useLocalStorage } from '@/hooks/use-local-storage';
import * as Collapsible from '@radix-ui/react-collapsible';
import { shouldProcessLinkClick, useIsSettings } from '../../../../utils/navigation';

import useSetupGuideExperiment from '@/features/experiments/setup-guide/hook';
import { useTrackEvent } from '@/features/track/hook';
import { useListWorkspaceObjectTypes } from '@/features/workspaces/hooks';
import { useExperiment } from '@/hooks/use-experiment';
import { useScrollbarWidth } from '@/hooks/use-scrollbar-width';
import { AccountState } from '@/utils/account';
import { useSampleWorkspace } from '@/utils/sample-workspace';
import {
  Icon,
  Label,
  List,
  ListItem,
  Popover,
  PopoverContent,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import SetupGuide from './components/setup-guide';

const NAV_COLLAPSED_LOCAL_STORAGE_KEY = 'hydra:is-main-nav-collapsed';

const MainNavContext = createContext<{ isNavCollapsed: boolean }>({
  isNavCollapsed: false,
});
function useMainNavContext() {
  return useContext(MainNavContext);
}

function LinkItem(props: {
  item: NavItem;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => unknown;
  activeApp: AppBundle | null;
  inPopover?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // When the nav is in the collapsed state and a "group" link is clicked, we
  // want to show a popover with the links
  const { isNavCollapsed } = useMainNavContext();
  const currentWorkspace = useCurrentWorkspace();
  const { isSampleWorkspace } = useSampleWorkspace();
  const [isChildLinkPopoverOpen, setIsChildLinkPopoverOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const activeRoute = useStore(activeRouteStore);
  const shouldShowTooltip = isNavCollapsed && !(props.inPopover ?? false) && !isChildLinkPopoverOpen;

  let itemProps = { ...props.item };
  /**
   * Allow prop override when on the sample workspace
   * Override only occurs when alternate props are available, otherwise the default apply
   */
  if (isSampleWorkspace(currentWorkspace?.id)) {
    itemProps = { ...itemProps, ...props.item.onSampleWorkspace };
  }
  const {
    iconName,
    href,
    label,
    id,
    showPremiumLabel = false,
    showNewLabel = false,
    variant,
    showIconOnExpand,
  } = itemProps;
  const hideIcon = iconName == null || (showIconOnExpand === false && !isNavCollapsed);

  if (href != null) {
    return (
      <Tooltip content={label} isDisabled={!shouldShowTooltip} placement="right" isCustom={true}>
        {(context) => (
          <a
            className={clsx('main-nav__main-link', {
              'main-nav__main-link--active': isNavItemActive(props.item, activeRoute),
              'main-nav__main-link--in-popover': props.inPopover ?? false,
              'main-nav__main-link--verdant': variant === 'verdant',
              'main-nav__main-link--evergreen': variant === 'evergreen',
            })}
            href={href}
            data-test-nav-item={id}
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps({
              onClick: (event: MouseEvent<HTMLAnchorElement>) => props.onClick(event, props.item),
            })}
          >
            {hideIcon === false ? (
              <div className="main-nav__main-link-icon">
                <Icon name={iconName} size="md" />
              </div>
            ) : null}
            <div className="main-nav__main-link-label">{label}</div>

            {showNewLabel ? <Label color="purple">New</Label> : null}

            {showPremiumLabel ? (
              // -2px margin to create a little bit more space - "Collections"
              // currently gets ellipsisized otherwise
              <Label color="plum" style={{ marginLeft: '-2px' }}>
                Premium
              </Label>
            ) : null}
          </a>
        )}
      </Tooltip>
    );
  }

  if (props.item.children != null) {
    const isAnyChildActive = props.item.children.some(
      (child) => child.href != null && isNavItemActive(child, activeRoute),
    );

    const children = props.item.children.map((child: NavItem) => {
      return <LinkItem key={child.id} item={child} onClick={props.onClick} activeApp={props.activeApp} />;
    });

    let isGroupButtonActive = isAnyChildActive;

    // Show the button as active if the current route is any
    // of this group's children, and: if the nav is in a collapsed state OR
    // the group is collapsed
    isGroupButtonActive = isGroupButtonActive && (!isExpanded || isNavCollapsed);

    return (
      <Collapsible.Root open={isExpanded}>
        <Popover
          withArrow={false}
          placement="right"
          isOpen={isChildLinkPopoverOpen}
          onOpenChange={(open) => setIsChildLinkPopoverOpen(open)}
          shouldTriggerOnClick={isNavCollapsed}
        >
          {(popoverContext) => (
            <Tooltip
              placement="right"
              isCustom={true}
              isOpen={isTooltipOpen && isNavCollapsed}
              isDisabled={isChildLinkPopoverOpen && !isTooltipOpen}
              onOpenChange={(open) => {
                setIsTooltipOpen(open);
              }}
            >
              {(tooltipContext) => (
                <>
                  <m.button
                    className={clsx('main-nav__main-link', 'main-nav__main-link--collapsible', {
                      'main-nav__main-link--active': isGroupButtonActive,
                    })}
                    type="button"
                    initial={false}
                    ref={(node) => {
                      popoverContext.reference.setReference(node);
                      tooltipContext.reference.setReference(node);
                    }}
                    {...popoverContext.reference.getReferenceProps({
                      ...tooltipContext.reference.getReferenceProps({
                        onClick: () => {
                          if (!isNavCollapsed) {
                            setIsExpanded((expanded) => !expanded);
                          }
                        },
                      }),
                    })}
                  >
                    {props.item.iconName != null ? (
                      <div className="main-nav__main-link-icon">
                        <Icon name={props.item.iconName} size="md" />
                      </div>
                    ) : null}
                    <div className="main-nav__main-link-label" data-test-main-nav={props.item.label}>
                      {props.item.label}
                    </div>
                    <div className="main-nav__main-link-chevron-icon-container">
                      <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size="sm" />
                    </div>
                  </m.button>
                  <TooltipContent>{props.item.label}</TooltipContent>
                </>
              )}
            </Tooltip>
          )}

          <PopoverContent className="main-nav__main-link-group-popover main-nav-styles" p="0">
            <List styleType="none" className="main-nav__main-link-group-popover-list">
              {props.item.children.map((child: NavItem) => {
                return (
                  <ListItem key={child.id} className="main-nav__main-link-group-popover-list-item">
                    <LinkItem
                      item={child}
                      onClick={(event, item) => {
                        props.onClick(event, item);
                        setIsChildLinkPopoverOpen(false);
                      }}
                      activeApp={props.activeApp}
                      inPopover={true}
                    />
                  </ListItem>
                );
              })}
            </List>
          </PopoverContent>
        </Popover>
        <Collapsible.Content className="main-nav__main-link-collapsible-content">
          <section className="main-nav__main-link-children">{children}</section>
        </Collapsible.Content>
      </Collapsible.Root>
    );
  }

  return <div></div>;
}

export function MainNavigation() {
  const [navItemGroups, setNavItemGroups] = useState<JSX.Element[]>([]);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const currentWorkspace = useCurrentWorkspace();
  const permissions = usePermissions();
  const auth = useStore(authStore);
  const safeNavigate = useSafeNavigate();
  const activeApp = useActiveBundle();
  const themeContext = useThemeContext();
  const { isSampleWorkspace } = useSampleWorkspace();
  const account = useStore(accountStore);
  const track = useTrackEvent();
  const journeysObjectTypes = useListWorkspaceObjectTypes(currentWorkspace?.id);
  const isTrial = auth?.account?.account_state === AccountState.Free;
  const mainNavMainElementRef = useRef<HTMLDivElement>(null);

  // setup guide nav setup
  const [isNavCollapsed, setIsNavCollapsed] = useLocalStorage<boolean>(NAV_COLLAPSED_LOCAL_STORAGE_KEY, false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const { sampleWorkspaceId } = useSampleWorkspace();
  const discoverInAppExperiment = useExperiment('discoverInApp') === 'treatment';

  const {
    isLoading: isSetupGuideLoading,
    setupGuide,
    shouldHideSetupGuide,
    shouldHideTrialProgress,
  } = useSetupGuideExperiment();

  const { isSettingsPage } = useIsSettings();
  const asideOnly = activeApp == null;

  useEffect(() => {
    if (isSetupGuideLoading) return;

    if (activeApp !== AppBundle.Journeys) {
      setShowSetupGuide(false);
      return;
    }
    if (!shouldHideSetupGuide) {
      setShowSetupGuide(true);
    }
  }, [
    currentWorkspace?.id,
    activeApp,
    isNavCollapsed,
    isSetupGuideLoading,
    shouldHideSetupGuide,
    shouldHideTrialProgress,
  ]);

  // generating navigation items and groups
  useEffect(() => {
    const onClickNavItem = (event: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => {
      if (!shouldProcessLinkClick(event)) return;

      if (item.id === 'invite-teammates') {
        track('Team member invite: clicked from side nav', {
          isSampleWorkspace: isSampleWorkspace(currentWorkspace?.id),
        });
      }
      if (item.id === 'view-workspace') {
        track(`View workspace: clicked from side nav`, {
          isSampleWorkspace: isSampleWorkspace(currentWorkspace?.id),
        });
        /**
         * We return early here so that we do not safeNavigate, and instead hard refresh
         * this is because permissions can get out of sync otherwise
         * */
        return;
      }

      event.preventDefault();
      if (item.href == null) return;

      safeNavigate(item.href, item.transitionOptions ?? {});
    };

    let items: NavItem[][] = [];

    if (currentWorkspace != null) {
      const objectTypes = journeysObjectTypes.data != null ? journeysObjectTypes.data : [];
      items = generateRoutes(currentWorkspace, auth, permissions, {
        showSetupGuide,
        discoverInAppExperiment,
        sampleWorkspaceId,
        shouldShowHome:
          hasFeatureFlag(AccountFeatureFlag.TrialRebootLite) === true &&
          !isSampleWorkspace(currentWorkspace.id) &&
          account?.account_state === AccountState.Free,
        objectTypes,
      });
    }

    if (
      isSettingsPage &&
      auth.user?.last_cio_workspace_id != null &&
      auth.user?.last_cio_workspace_id !== '' &&
      currentWorkspace != null
    ) {
      items = generateRoutes(currentWorkspace, auth, permissions);
    }

    const groups = items.map((group, i) => {
      let isTailGroup = false;

      const navItems = group.map((item) => {
        if (item.id === 'invite-teammates' || item.id === 'view-workspace') {
          isTailGroup = true;
        }

        if (item.showSetupGuide === true && !isNavCollapsed) {
          return <SetupGuide key={currentWorkspace?.id} setupGuide={setupGuide} />;
        }

        return <LinkItem key={item.id} item={item} onClick={onClickNavItem} activeApp={activeApp} />;
      });

      const className =
        i === items.length - 1 && isTailGroup ? `main-nav__tail-group main-nav__main-group` : `main-nav__main-group`;

      return (
        <div key={`group_${i}`} className={className}>
          {navItems}
        </div>
      );
    });
    setNavItemGroups(groups);
  }, [
    activeApp,
    currentWorkspace?.id,
    isSettingsPage,
    showSetupGuide,
    isNavCollapsed,
    safeNavigate,
    journeysObjectTypes.data,
    isTrial,
  ]);

  useMemo(() => {
    const width = useScrollbarWidth();
    setScrollbarWidth(width);
  }, []);

  const onExpandCollapseButtonClick = () => {
    setIsNavCollapsed((isCollapsed) => !isCollapsed);
    track('main-nav', { isCollapsed: isNavCollapsed });
  };

  useEffect(() => {
    const NAV_WIDTHS = {
      withoutAside: {
        collapsed: 64,
        expanded: 226,
      },
      asideOnly: {
        collapsed: 50,
        expanded: 50,
      },
    };

    let navWidths;
    if (asideOnly) {
      navWidths = NAV_WIDTHS.asideOnly;
    } else {
      navWidths = NAV_WIDTHS.withoutAside;
    }

    let navWidth = 0;
    if (isNavCollapsed) {
      navWidth = navWidths.collapsed;
      const element = mainNavMainElementRef.current;
      if (element != null && element.scrollHeight > element.clientHeight) {
        navWidth += scrollbarWidth;
      }
    } else {
      navWidth = navWidths.expanded;
    }

    themeContext.setVariable(GlobalCSSVariable.MainNavWidth, `${navWidth}px`);
  }, [asideOnly, isNavCollapsed, themeContext, navItemGroups]);

  const navTitle = 'Journeys';

  return (
    <MainNavContext.Provider value={{ isNavCollapsed }}>
      <div
        className={clsx(
          'main-nav-container',
          'main-nav-styles',
          { 'main-nav-container--aside-only': asideOnly },
          { 'main-nav-container--collapsed': isNavCollapsed },
        )}
      >
        <div className="main-nav">
          <div className="main-nav__body">
            <div
              className={'main-nav__main main-nav__main--consolidated'}
              ref={mainNavMainElementRef}
              data-test-main-nav={navTitle}
            >
              {navItemGroups}
              <div
                className={clsx('main-nav__main-title-container main-nav-collapsed-toggle', {
                  'main-nav__main-title-container--is-collapsed': isNavCollapsed,
                })}
              >
                <ExpandCollapseButton isCollapsed={isNavCollapsed} onClick={onExpandCollapseButtonClick} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainNavContext.Provider>
  );
}
